import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import logo from "@/assets/skylink-logo.png";
import Footer from "@/components/Footer";
import { ThemeToggle } from "@/components/ThemeToggle";

const Auth = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    // 1) Subscribe FIRST to avoid missing PASSWORD_RECOVERY event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsResettingPassword(true);
        setIsForgotPassword(false);
        setIsLogin(false);
      }
    });

    // 2) Detect recovery via URL params/fragments as fallback
    try {
      const url = new URL(window.location.href);
      const fromQuery = url.searchParams.get('type');
      const fromHash = new URLSearchParams(url.hash.replace('#', '')).get('type');
      if (fromQuery === 'recovery' || fromHash === 'recovery') {
        setIsResettingPassword(true);
        setIsForgotPassword(false);
        setIsLogin(false);
        return () => subscription.unsubscribe();
      }
    } catch {}

    // 3) Only then check existing session and redirect
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate("/");
      }
    };
    checkUser();

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isResettingPassword) {
        // Handle password update after clicking reset link
        if (password !== confirmPassword) {
          throw new Error("Passwords do not match");
        }
        
        // Password validation: minimum 12 characters with complexity requirements
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{12,}$/;
        if (password.length < 12) {
          throw new Error("Password must be at least 12 characters long");
        }
        if (!passwordRegex.test(password)) {
          throw new Error("Password must contain uppercase, lowercase, number, and special character");
        }

        const { error } = await supabase.auth.updateUser({
          password: password,
        });

        if (error) throw error;
        toast.success("Password updated successfully! Please log in.");
        setIsResettingPassword(false);
        setIsLogin(true);
        setPassword("");
        setConfirmPassword("");
      } else if (isForgotPassword) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth`,
        });

        if (error) throw error;
        toast.success("Password reset link sent to your email!");
        setIsForgotPassword(false);
        setIsLogin(true);
      } else if (isLogin) {
        // SECURITY: Password sent over HTTPS to Supabase Auth which verifies against hashed password
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;
        
        // Clear password from memory
        setPassword("");
        
        toast.success("Logged in successfully");
        navigate("/");
      } else {
        // Validate inputs
        if (!email || !password || !fullName) {
          throw new Error("All fields are required");
        }
        // Password validation: minimum 12 characters with complexity requirements
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{12,}$/;
        if (password.length < 12) {
          throw new Error("Password must be at least 12 characters long");
        }
        if (!passwordRegex.test(password)) {
          throw new Error("Password must contain uppercase, lowercase, number, and special character");
        }
        if (fullName.trim().length < 2 || fullName.length > 100) {
          throw new Error("Name must be between 2 and 100 characters");
        }

        // SECURITY: Password sent over HTTPS and automatically hashed by Supabase Auth using bcrypt
        const { error } = await supabase.auth.signUp({
          email: email.trim(),
          password, // Transmitted securely, immediately hashed, never stored as plain text
          options: {
            emailRedirectTo: `${window.location.origin}/`,
            data: {
              full_name: fullName.trim(),
            },
          },
        });

        if (error) throw error;
        
        // Clear sensitive data from memory
        setPassword("");
        setConfirmPassword("");
        
        toast.success("Account created successfully! Please log in.");
        setIsLogin(true);
      }
    } catch (error: any) {
      toast.error(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 relative">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img src={logo} alt="Skylink" className="h-12" />
          </div>
          <CardTitle className="text-2xl">
            Skylink Management Portal
          </CardTitle>
          <CardDescription>
            {isResettingPassword 
              ? "Enter your new password" 
              : isForgotPassword 
              ? "Reset your password" 
              : isLogin 
              ? "Sign in to your account" 
              : "Create a new account"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAuth} className="space-y-4">
            {!isLogin && !isForgotPassword && !isResettingPassword && (
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                  placeholder="John Doe"
                />
              </div>
            )}
            {!isResettingPassword && (
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="instructor@skylink.com"
                />
              </div>
            )}
            {!isForgotPassword && (
              <div className="space-y-2">
                <Label htmlFor="password">{isResettingPassword ? "New Password" : "Password"}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    minLength={12}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </Button>
                </div>
                {!isLogin && (
                  <p className="text-xs text-muted-foreground">
                    Min 12 characters with uppercase, lowercase, number & special character
                  </p>
                )}
              </div>
            )}
            {isResettingPassword && (
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    minLength={12}
                    className="pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </Button>
                </div>
              </div>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading 
                ? "Loading..." 
                : isResettingPassword 
                ? "Update Password" 
                : isForgotPassword 
                ? "Send Reset Link" 
                : isLogin 
                ? "Sign In" 
                : "Sign Up"}
            </Button>
          </form>
          {!isResettingPassword && (
            <div className="mt-4 text-center text-sm space-y-2">
              {isLogin && !isForgotPassword && (
                <button
                  type="button"
                  onClick={() => {
                    setIsForgotPassword(true);
                    setIsLogin(false);
                  }}
                  className="text-primary hover:underline block w-full"
                >
                  Forgot password?
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setIsForgotPassword(false);
                  setIsLogin(!isLogin);
                }}
                className="text-primary hover:underline block w-full"
              >
                {isForgotPassword
                  ? "Back to sign in"
                  : isLogin
                  ? "Don't have an account? Sign up"
                  : "Already have an account? Sign in"}
              </button>
            </div>
          )}
        </CardContent>
      </Card>
      <Footer />
    </div>
  );
};

export default Auth;